import React, { FocusEvent } from 'react';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import {
  useController,
  UseControllerProps,
  FieldValues,
  Path,
  PathValue,
} from 'react-hook-form';
import { InputBaseProps, TextField, TextFieldProps } from '@mui/material';

export type LbNumericFormatInputProps<T extends FieldValues, TName extends Path<T>> = {
  selectValueOnFocus?: boolean;
  dataTestId?: string;
  InputProps?: InputBaseProps;
} & UseControllerProps<T, TName> &
  Pick<
    TextFieldProps,
    | 'label'
    | 'variant'
    | 'size'
    | 'helperText'
    | 'className'
    | 'placeholder'
    | 'disabled'
    | 'autoFocus'
    | 'margin'
  > &
  Pick<
    NumericFormatProps,
    'prefix' | 'suffix' | 'allowNegative' | 'decimalScale' | 'fixedDecimalScale'
  >;

/**
 * @description Used to change view format of numbers (mask, add prefix...) but save it like number
 */

const LbNumericFormatInput = <T extends FieldValues, TName extends Path<T>>({
  control,
  name,
  rules,
  defaultValue = '' as PathValue<T, TName>,
  shouldUnregister,
  helperText,
  selectValueOnFocus,
  InputProps,
  dataTestId,
  ...props
}: LbNumericFormatInputProps<T, TName>) => {
  const {
    field: { onChange, ref, ...field },
    fieldState: { error },
  } = useController({
    control,
    name,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const handlerFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (selectValueOnFocus) {
      event.target.select();
    }
  };

  const isRequired = !!rules?.required;
  return (
    <NumericFormat
      // connect to react-hook-form
      {...field}
      onValueChange={(value, { source }) => {
        // 'prop' = the value prop changed (setValue, reset): only user input reaches the form
        if (source === 'event') {
          onChange(value.floatValue ?? ''); // an emptied input gives undefined
        }
      }}
      valueIsNumericString
      // MUI TextField props
      customInput={TextField}
      onFocus={handlerFocus}
      helperText={error?.message || helperText}
      error={!!error}
      required={isRequired}
      fullWidth
      inputRef={ref}
      slotProps={{
        input: InputProps,
      }}
      data-test-id={dataTestId}
      // add config props
      {...props}
    />
  );
};

export { LbNumericFormatInput };
